import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import TechnicianService from "src/Services/API/technician.service";
import { getAvatarUrl, getImageUrl } from "src/Common/common";
import { can, handleError } from "src/Common/function";
import useTeamTechStatusRealtime from "src/hooks/useTeamTechStatusRealtime";
import TechActivityDrawer from "./TechActivityDrawer";

// techStatus values: 1 Checked Out, 2 Enroute, 3 On Site, 4 Getting Parts.
// Columns are ordered to match the status board design.
const COLUMNS = [
  {
    status: 3,
    label: "ON SITE",
    color: "#15803D",
    headerBg: "#F0FDF4",
    headerBorder: "#86EFAC",
    dotColor: "#22C55E",
    badgeColor: "#DCFCE7",
  },
  {
    status: 2,
    label: "ENROUTE",
    color: "#C2410C",
    headerBg: "#FFF7ED",
    headerBorder: "#FDBA74",
    dotColor: "#F97316",
    badgeColor: "#FFEDD5",
  },
  {
    status: 4,
    label: "GETTING PARTS",
    color: "#7E22CE",
    headerBg: "#FAF5FF",
    headerBorder: "#D8B4FE",
    dotColor: "#A855F7",
    badgeColor: "#F3E8FF",
  },
  {
    status: 1,
    label: "CHECKED OUT",
    color: "#334155",
    headerBg: "#EEF0F9",
    headerBorder: "#C7CBD9",
    dotColor: "#868DA6",
    badgeColor: "#EEF0F9",
  },
];

function Status({ branch }) {
  const [techs, setTechs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTech, setSelectedTech] = useState(null);

  const loadStatusBoard = useCallback(() => {
    // The status board is gated by user:readStatus; skip the call (and its 403)
    // when the user can't read it.
    if (!can("user:readStatus")) {
      setIsLoading(false);
      return;
    }
    TechnicianService.getStatusBoard(branch)
      .then((response) => {
        if (response.status === 200) {
          setTechs(response.data);
        }
      })
      .catch((error) => handleError(error))
      .finally(() => setIsLoading(false));
  }, [branch]);

  useEffect(() => {
    setIsLoading(true);
    loadStatusBoard();
  }, [loadStatusBoard]);

  // A tech status change anywhere in the team affects which column a card
  // belongs to (and the active-job cascade flips others to Checked Out), so the
  // simplest correct response is to re-fetch the board.
  const handleTechStatusChanged = useCallback(() => {
    loadStatusBoard();
  }, [loadStatusBoard]);

  useTeamTechStatusRealtime({ onTechStatusChanged: handleTechStatusChanged });

  const techsByStatus = useMemo(() => {
    const groups = { 1: [], 2: [], 3: [], 4: [] };
    techs.forEach((tech) => {
      if (groups[tech.techStatus]) {
        groups[tech.techStatus].push(tech);
      }
    });
    return groups;
  }, [techs]);

  return (
    <>
      <div className="grid h-full min-h-0 grid-cols-1 gap-4 pt-6 sm:grid-cols-2 xl:grid-cols-4">
        {COLUMNS.map((column) => (
          <StatusColumn
            key={column.status}
            column={column}
            techs={techsByStatus[column.status]}
            isLoading={isLoading}
            onTechClick={setSelectedTech}
          />
        ))}
      </div>

      <TechActivityDrawer isOpen={!!selectedTech} tech={selectedTech} onClose={() => setSelectedTech(null)} />
    </>
  );
}

export default Status;

const StatusColumn = ({ column, techs, isLoading, onTechClick }) => {
  return (
    <div className="flex min-h-0 flex-col rounded-[12px] bg-white">
      <div
        className="flex flex-shrink-0 items-center justify-between rounded-t-[12px] px-4 h-[44px]"
        style={{ background: column.headerBg, border: `1px solid ${column.headerBorder}` }}
      >
        <div className="flex items-center gap-2">
          <span className="w-[8px] h-[8px] rounded-full" style={{ background: column.dotColor }} />
          <span
            className="font-OpenSansRegular font-[700] text-[14px] leading-[20px] tracking-[0.14px] uppercase"
            style={{ color: column.color }}
          >
            {column.label}
          </span>
        </div>
        <span
          className="flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full font-OpenSansRegular font-[600] text-[12px]"
          style={{ background: column.badgeColor, color: column.color }}
        >
          {techs.length}
        </span>
      </div>

      <div className="flex-1 min-h-0 p-2 overflow-y-auto rounded-b-[12px] border border-t-0 border-[#E5E8F3]">
        {isLoading ? (
          <div className="px-2 py-6 text-center font-OpenSansRegular text-[12px] text-[#7C829A]">Loading…</div>
        ) : techs.length === 0 ? (
          <div className="px-2 py-6 text-center font-OpenSansRegular text-[12px] text-[#7C829A]">No technicians</div>
        ) : (
          <div className="flex flex-col gap-2">
            {techs.map((tech) => (
              <TechCard key={tech.id} tech={tech} onClick={onTechClick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TechCard = ({ tech, onClick }) => {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(tech)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(tech);
        }
      }}
      className="cursor-pointer rounded-[10px] border border-[#EEF0F6] bg-white p-3 transition-shadow hover:shadow-[0_1px_3px_0_rgba(0,0,0,0.08)]"
    >
      <div className="flex items-start gap-3">
        <Avatar user={tech.user} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="font-OpenSansRegular font-[600] text-[14px] leading-[20px] tracking-[0.14px] text-[#1E293B] capitalize truncate">
              {tech.user?.name ?? "—"}
            </div>
            {tech.wo?.branch && (
              <span className="font-OpenSansRegular font-[600] text-[12px] leading-[20px] text-[#7C829A] whitespace-nowrap shrink-0">
                {tech.wo.branch}
              </span>
            )}
          </div>
          {((tech.wo?.number && tech.wo?.id) || tech.updatedAt) && (
            <div className="flex items-center justify-between gap-2">
              {tech.wo?.number && tech.wo?.id ? (
                <a
                  href={`/work/${tech.wo.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-block min-w-0 max-w-full font-OpenSansRegular font-[600] text-[12px] leading-[18px] tracking-[0.12px] text-[#0065FD] capitalize truncate hover:underline"
                >
                  WO-{tech.wo.number}
                </a>
              ) : (
                <span />
              )}
              <span className="font-OpenSansRegular text-[12px] leading-[18px] text-[#9CA1B3] whitespace-nowrap shrink-0">
                {tech.updatedAt ? moment(tech.updatedAt).fromNow(true) + " ago" : ""}
              </span>
            </div>
          )}

          {(tech.wo?.customer || tech.wo?.location) && (
            <div className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A] capitalize line-clamp-2">
              {[tech.wo?.customer, tech.wo?.location].filter(Boolean).join(" - ")}
            </div>
          )}

          <div className="mt-2 flex items-end justify-between gap-2 border-t border-[#EEF0F6] pt-2">
            <span className="font-OpenSansRegular font-[600] text-[11px] leading-[18px] text-[#475569] whitespace-pre-line break-words">
              {tech.wo?.description || "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Avatar = ({ user }) => {
  const [errored, setErrored] = useState(false);
  const showImage = user?.avatar && !errored;

  return showImage ? (
    <img
      src={getAvatarUrl(user.avatar)}
      onError={() => setErrored(true)}
      className="w-[36px] h-[36px] rounded-full object-cover shrink-0"
      alt={user?.name ?? ""}
    />
  ) : (
    <img
      src={getImageUrl("/images/common/avatar_default.png")}
      className="w-[36px] h-[36px] rounded-full object-cover shrink-0"
      alt=""
    />
  );
};
